import {
  Button,
  Checkbox,
  Container,
  MultiSelect,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { notifications } from "@mantine/notifications";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import {
  exportCPTExcel,
  exportRawTestData,
  TestExportResponse,
} from "@utils/api-communication/RestApiCli";
import { CONFIGURATION_VALUE_LABEL_KEY_MAP } from "@constants/uiConstants";
import { TestType } from "types/tests";
import { parseDateOnlyOrNull } from "@utils/CommonUtil";

function toIsoDate(date: Date) {
  return date.toISOString().split("T")[0];
}

function triggerDownload(result: TestExportResponse) {
  if (result.blob) {
    const objectUrl = URL.createObjectURL(result.blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = result.filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);
    return;
  }

  if (!result.downloadUrl) {
    throw new Error("Export completed but no file content was returned.");
  }

  window.location.assign(result.downloadUrl);
}

function startExportNotification(): string {
  const notificationId = `export-${uuidv4()}`;
  notifications.show({
    id: notificationId,
    title: "Preparing export",
    message: "Starting…",
    loading: true,
    autoClose: false,
    withCloseButton: false,
  });
  return notificationId;
}

function updateExportProgress(notificationId: string, message: string | null) {
  notifications.update({
    id: notificationId,
    title: "Preparing export",
    message,
    loading: true,
    autoClose: false,
    withCloseButton: false,
  });
}

function finishExport(
  notificationId: string,
  title: string,
  message: string,
  color: "teal" | "blue" | "red",
) {
  notifications.update({
    id: notificationId,
    title,
    message,
    loading: false,
    color,
    autoClose: color === "red" ? 6000 : 4000,
    withCloseButton: true,
  });
}

function notifyValidationError(message: string) {
  notifications.show({
    title: "Export",
    message,
    color: "red",
    autoClose: 5000,
  });
}

function resolveExportErrorMessage(downloadError: any): string {
  if (downloadError?.response?.status === 403) {
    return "This export is not enabled for your organization.";
  }
  return downloadError?.message ?? "Export failed. Please try again.";
}

export default function DataExportPage() {
  const { t } = useTranslation();
  const [isExcelLoading, setIsExcelLoading] = useState(false);

  const [exportAllTestTypes, setExportAllTestTypes] = useState(true);
  const [selectedTestTypes, setSelectedTestTypes] = useState<string[]>([]);
  const [rawDateFrom, setRawDateFrom] = useState<Date | null>(null);
  const [rawDateTo, setRawDateTo] = useState<Date | null>(null);
  const [isRawLoading, setIsRawLoading] = useState(false);

  const testTypeOptions = useMemo(
    () =>
      Object.entries(TestType)
        .map(([enumKey, value]) => {
          const labelKey =
            CONFIGURATION_VALUE_LABEL_KEY_MAP[
              enumKey as keyof typeof CONFIGURATION_VALUE_LABEL_KEY_MAP
            ]?.labelKey;
          return {
            value,
            label: labelKey ? t(labelKey) : value,
          };
        })
        .sort((first, second) => first.label.localeCompare(second.label)),
    [t],
  );

  const handleRawDownload = async () => {
    if (!rawDateFrom) {
      notifyValidationError("Please choose a start date before downloading.");
      return;
    }

    if (!exportAllTestTypes && selectedTestTypes.length === 0) {
      notifyValidationError("Select at least one test type, or choose to export all.");
      return;
    }

    if (rawDateTo && rawDateTo < rawDateFrom) {
      notifyValidationError("The end date cannot be earlier than the start date.");
      return;
    }

    setIsRawLoading(true);
    const notificationId = startExportNotification();

    try {
      const result = await exportRawTestData(
        {
          testTypes: exportAllTestTypes ? undefined : (selectedTestTypes as TestType[]),
          dateFrom: toIsoDate(rawDateFrom),
          dateTo: rawDateTo ? toIsoDate(rawDateTo) : undefined,
        },
        {
          onStatusChange: (message) => updateExportProgress(notificationId, message),
        },
      );

      if (!result) {
        finishExport(notificationId, "Export", "No matching records were found.", "blue");
        return;
      }

      triggerDownload(result);
      finishExport(
        notificationId,
        "Export ready",
        `Download started for ${result.filename}`,
        "teal",
      );
    } catch (downloadError: any) {
      finishExport(
        notificationId,
        "Export failed",
        resolveExportErrorMessage(downloadError),
        "red",
      );
    } finally {
      setIsRawLoading(false);
    }
  };

  const handleExcelDownload = async () => {
    setIsExcelLoading(true);
    const notificationId = startExportNotification();

    try {
      const result = await exportCPTExcel({
        onStatusChange: (message) => updateExportProgress(notificationId, message),
      });

      if (!result) {
        finishExport(notificationId, "Export", "No CPT records were found.", "blue");
        return;
      }

      triggerDownload(result);
      finishExport(
        notificationId,
        "Export ready",
        `Download started for ${result.filename}`,
        "teal",
      );
    } catch (downloadError: any) {
      finishExport(
        notificationId,
        "Export failed",
        resolveExportErrorMessage(downloadError),
        "red",
      );
    } finally {
      setIsExcelLoading(false);
    }
  };

  return (
    <Container py="md">
      <Stack gap="md">
        <div>
          <Title>Data Export</Title>
          <Text c="dimmed" size="sm">
            Download test data in various formats.
          </Text>
        </div>

        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          <Paper withBorder p="xl" radius="md">
            <Stack gap="md">
              <Text fw={600} size="lg">
                Raw JSON Export (by test type)
              </Text>
              <Text c="dimmed" size="sm">
                Exports raw test data as JSON. Select specific test types or export all of them, and
                filter by date range.
              </Text>

              <Checkbox
                label="Export all test types"
                checked={exportAllTestTypes}
                onChange={(event) => setExportAllTestTypes(event.currentTarget.checked)}
              />

              <MultiSelect
                label="Test types"
                placeholder={selectedTestTypes.length === 0 ? "Select test types" : undefined}
                data={testTypeOptions}
                value={selectedTestTypes}
                onChange={setSelectedTestTypes}
                disabled={exportAllTestTypes}
                searchable
                clearable
              />

              <DatePickerInput
                label="Date from"
                placeholder="Pick a date"
                value={rawDateFrom}
                onChange={(v) => setRawDateFrom(parseDateOnlyOrNull(v))}
                clearable
              />
              <DatePickerInput
                label="Date to"
                placeholder="Pick a date"
                value={rawDateTo}
                onChange={(v) => setRawDateTo(parseDateOnlyOrNull(v))}
                clearable
              />

              <Button onClick={handleRawDownload} loading={isRawLoading}>
                Download JSON
              </Button>
            </Stack>
          </Paper>

          <Paper withBorder p="xl" radius="md">
            <Stack gap="md">
              <Text fw={600} size="lg">
                Excel Export (all CPT tests)
              </Text>
              <Text c="dimmed" size="sm">
                Exports all CPT tests as a flat Excel spreadsheet with patient info, raw metrics,
                performance scores, and z-scores.
              </Text>

              <Button onClick={handleExcelDownload} loading={isExcelLoading}>
                Download Excel
              </Button>
            </Stack>
          </Paper>
        </SimpleGrid>
      </Stack>
    </Container>
  );
}
